import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, CheckIcon } from 'lucide-react';
import { categories } from '../../data/conditions';
import { AssigneeSelect } from './AssigneeSelect';
import { InlineDateField } from './InlineDateField';
import { InlineTextField } from './InlineTextField';
import { RowActions } from './RowActions';
import { StatusSelect } from './StatusSelect';
import { conditionRowGrid, daysOpen, daysPastEta, etaApplies, formatDate, getEscalation, isResolved } from '../../utils/conditions';
import type { ConditionPatch } from '../../hooks/useConditions';
import type { Condition } from '../../types/conditions';
interface ConditionRowProps {
  condition: Condition;
  selected: boolean;
  onToggleSelect: () => void;
  onUpdate: (patch: ConditionPatch) => void;
  onComments: () => void;
  onUpload: () => void;
  onLinkDocuments: () => void;
  showCategory?: boolean;
}
export function ConditionRow({
  condition,
  selected,
  onToggleSelect,
  onUpdate,
  onComments,
  onUpload,
  onLinkDocuments,
  showCategory = false
}: ConditionRowProps) {
  const escalation = getEscalation(condition);
  const resolved = isResolved(condition.status);
  const overdue = daysPastEta(condition);
  const name = condition.name;
  return <motion.div layout initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18
  }} role="row" className={`${conditionRowGrid} border-b border-slate-100 px-5 py-3 transition-colors last:border-b-0 ${selected ? 'bg-brand/[0.04]' : 'hover:bg-slate-50'} ${escalation !== 'none' || overdue > 0 ? 'shadow-[inset_3px_0_0_0_#dc2626]' : ''}`} data-id="element-90" data-mp="2oqi85nl00wj">
      <div role="cell" data-id="element-91" data-mp="lmjnhelogmx3">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} aria-label={`Select ${name}`} className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand" data-id="element-92" data-mp="llrp5w0p4b34" />
      </div>

      <div role="cell" className="min-w-0" data-id="element-93" data-mp="1nzcp1jdgyrsd">
        <InlineTextField value={condition.name} onSave={v => v && onUpdate({
        name: v
      })} ariaLabel="condition name" textClassName={`text-sm font-semibold ${resolved ? 'text-slate-500' : 'text-slate-900'}`} data-id="element-94" data-mp="1ny1k3g8afdzo" />
        <InlineTextField value={condition.description} onSave={v => onUpdate({
        description: v
      })} ariaLabel={`description for ${name}`} placeholder="Add description…" textClassName="text-xs text-slate-500" data-id="element-95" data-mp="19zl5l346oj6z" />
        {showCategory && <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-96" data-mp="vcrvqddfo5m6">
            {categories.find(cat => cat.id === condition.category)?.label}
          </span>}
        {escalation !== 'none' && <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-red-700" data-id="element-97" data-mp="hbp8vyem6ra7">
            <AlertTriangleIcon className="h-3 w-3" aria-hidden="true" data-id="element-98" data-mp="2t03wveg671v8" />
            No ETA or notes · {daysOpen(condition)}d —{' '}
            {escalation === 'avp' ? 'escalated to AVP of Processing' : 'processor emailed'}
          </p>}
      </div>

      <div role="cell" data-id="element-99" data-mp="31y5z1xbn40k3">
        <StatusSelect value={condition.status} onChange={status => onUpdate({
        status
      })} conditionName={name} data-id="element-100" data-mp="36zlmfw1aij5n" />
      </div>

      <div role="cell" data-id="element-101" data-mp="2aa48yuw2fste">
        <InlineDateField value={condition.createdAt} onSave={v => v && onUpdate({
        createdAt: v
      })} ariaLabel={`Created date for ${name}`} emptyLabel="Set date" data-id="element-102" data-mp="2aanracpmoj7y" />
      </div>

      <div role="cell" data-id="element-103" data-mp="2o07ol30fwo9v">
        <InlineDateField value={condition.requestedAt} onSave={v => onUpdate({
        requestedAt: v
      })} ariaLabel={`Requested date for ${name}`} emptyLabel="Set date" data-id="element-104" data-mp="2o0r5cgect6mb" />
      </div>

      <div role="cell" data-id="element-105" data-mp="1vilvp4ulccdq">
        {resolved ? <div data-id="element-106" data-mp="217k3ho1gukv">
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-steel" data-id="element-107" data-mp="20ob4qgd9giz">Cleared</span>
            <InlineDateField value={condition.clearedAt} onSave={v => onUpdate({
          clearedAt: v
        })} ariaLabel={`Cleared date for ${name}`} emptyLabel="Set date" tone="success" data-id="element-108" data-mp="3t8aba93vn0v3" />
          </div> : !etaApplies(condition.status) ? <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600" title={condition.eta ? `ETA was ${formatDate(condition.eta)}` : 'Documents received'} data-id="element-109" data-mp="kqe2t4ou1m6o">
            <CheckIcon className="h-3 w-3 text-steel" aria-hidden="true" data-id="element-110" data-mp="pqinl6wrx32p" />
            ETA met
          </span> : <div data-id="element-111" data-mp="2vkibr8hcjnkc">
            <InlineDateField value={condition.eta} onSave={v => onUpdate({
          eta: v
        })} ariaLabel={overdue > 0 ? `ETA for ${name}, ${overdue} days overdue` : `ETA for ${name}`} emptyLabel="Set ETA" tone={overdue > 0 ? 'danger' : 'default'} data-id="element-112" data-mp="2vk0gof6f7fe0" />
            {overdue > 0 && <span className="mt-1 block text-[11px] font-semibold text-red-700" data-id="element-113" data-mp="27p6vlr7foxok">
                {overdue} {overdue === 1 ? 'day' : 'days'} past ETA
              </span>}
          </div>}
      </div>

      <div role="cell" data-id="element-114" data-mp="1xpg0poeyogny">
        <AssigneeSelect value={condition.assigneeId} onChange={assigneeId => onUpdate({
        assigneeId
      })} conditionName={name} data-id="element-115" data-mp="1xowjyb11rybi" />
      </div>

      <div role="cell" data-id="element-116" data-mp="1oquijudej3np">
        <RowActions condition={condition} onToggleEmail={() => onUpdate({
        includeInEmail: !condition.includeInEmail
      })} onToggleBorrowerVisible={() => onUpdate({
        borrowerVisible: !condition.borrowerVisible
      })} onComments={onComments} onUpload={onUpload} onLinkDocuments={onLinkDocuments} data-id="element-117" data-mp="1orm7imchu350" />
      </div>
    </motion.div>;
}